import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router';
import type { Organisation } from '@/api/model/Organisation';
import { useAuthStore } from '@/stores/authStore';
import { useOrganisationStore } from '@/stores/organisationStore';

type OrganisationStore = ReturnType<typeof useOrganisationStore>;

/** The organisation the URL names, or null for a route outside any. */
function routeOrganisation(to: RouteLocationNormalized): string | null {
    const wanted = to.params['organisation'];

    return typeof wanted === 'string' && wanted !== '' ? wanted : null;
}

/**
 * No memberships: either the profile has not loaded -- signed out, or a route
 * that loads none -- or the user genuinely belongs to nothing, in which case
 * every other screen is scoped to an organisation they do not have.
 */
function withoutMemberships(to: RouteLocationNormalized): true | RouteLocationRaw {
    if (!useAuthStore().loggedIn) {
        return true;
    }

    return to.name === 'no-organisations' ? true : { name: 'no-organisations' };
}

/** Somewhere real to be when the URL does not settle it. */
function fallbackFor(store: OrganisationStore): Organisation {
    return store.defaultOrganisation()!;
}

/**
 * Decide which organisation is active, from the URL.
 *
 * The URL is the single source of truth for this. `setOrganisations` records
 * the memberships without choosing between them, and this is the only thing
 * that writes `current_organisation` -- so there is exactly one decision, taken
 * in one place, per navigation. localStorage is only a record of the last
 * organisation seen, consulted when the URL names none.
 *
 * Registered after the auth guard, which has awaited the profile, so the
 * memberships are loaded by the time an id has to be resolved.
 *
 * Every screen reads `current_organisation` to decide what to fetch. Before
 * this existed, the store followed localStorage instead, so a cold load into
 * another organisation's URL rendered that URL with the previously-selected
 * organisation's data.
 */
/**
 * With memberships in hand: activate the one the URL names, or move the user
 * somewhere they can be.
 */
function withMemberships(to: RouteLocationNormalized, store: OrganisationStore): true | RouteLocationRaw {
    // They were shown the empty state and have since been invited in. Clicking
    // "Check again" reloads onto this route, and it is no longer where they
    // belong.
    if (to.name === 'no-organisations') {
        return { name: 'dashboard', params: { organisation: fallbackFor(store).public_id } };
    }

    const wanted = routeOrganisation(to);
    const organisation = wanted === null ? fallbackFor(store) : store.findOrganisationByPublicId(wanted);

    if (!organisation) {
        /*
         * A mistyped or unowned id. Leaving them here would show another
         * organisation's data under this URL -- the very thing this guard exists
         * to prevent -- so send them somewhere real. The dashboard rather than
         * the same route under a different organisation, because entity ids in
         * the path belong to the organisation that was asked for and resolve to
         * nothing under any other.
         */
        const fallback = fallbackFor(store);

        console.info(`Unknown organisation "${wanted}", redirecting to ${fallback.public_id}...`);

        return { name: 'dashboard', params: { organisation: fallback.public_id } };
    }

    store.useOrganisation(organisation);

    return true;
}

export function organisationGuard(to: RouteLocationNormalized): true | RouteLocationRaw {
    /*
     * Login, registration and password recovery live outside any organisation.
     * A signed-in user with no memberships is offered registration as a way out
     * of the empty state, so bouncing them back here would make that offer
     * impossible to accept.
     */
    if (!to.meta['requiresAuth']) {
        return true;
    }

    const store = useOrganisationStore();

    if (store.organisations.length === 0) {
        return withoutMemberships(to);
    }

    return withMemberships(to, store);
}
